"use client";

/**
 * Pre-paywall coverage gate (Decision #2 in
 * [[shared-subscription-any-household-adult-can-pay-co]]).
 *
 * Shown in place of the paywall when every household the user is a member
 * of already has an active subscription from another adult. Prevents
 * accidental redundant subscriptions and gives the user a clear path to
 * see who is covering them.
 */

import { useEffect, useState } from "react";
import { collection, doc, documentId, getDocs, query, where } from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { CoverageEntry } from "@/hooks/useCallerCoverage";
import type { Timestamp } from "firebase/firestore";

interface AlreadyCoveredModalProps {
  isOpen: boolean;
  onClose: () => void;
  entries: CoverageEntry[];
}

interface NameById {
  [uid: string]: string;
}

interface HouseholdNameById {
  [householdId: string]: string;
}

function formatDate(ts: Timestamp | null | undefined): string {
  if (!ts) return "—";
  try {
    return ts.toDate().toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

export function AlreadyCoveredModal({
  isOpen,
  onClose,
  entries,
}: AlreadyCoveredModalProps) {
  const [payerNames, setPayerNames] = useState<NameById>({});
  const [householdNames, setHouseholdNames] = useState<HouseholdNameById>({});

  useEffect(() => {
    if (!isOpen) return;
    const payerUids = Array.from(
      new Set(
        entries
          .map((e) => e.subscription?.payer_uid)
          .filter((u): u is string => typeof u === "string" && u !== ""),
      ),
    );
    const hhIds = entries.map((e) => e.householdId);
    void hydrateNames(payerUids, setPayerNames);
    void hydrateHouseholdNames(hhIds, setHouseholdNames);
  }, [isOpen, entries]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        role="presentation"
      />

      <div
        className="relative bg-white dark:bg-gray-900 w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="already-covered-title"
      >
        <div className="px-5 pt-5 pb-3 border-b border-gray-100 dark:border-white/5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2
                id="already-covered-title"
                className="text-xl font-bold text-gray-900 dark:text-gray-100"
              >
                {"You're already covered"}
              </h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {
                  "Every household you're part of already has an active subscription. You don't need to subscribe again."
                }
              </p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 shrink-0"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        <ul className="px-5 py-4 space-y-3">
          {entries.map((e) => {
            const sub = e.subscription;
            const payerUid = sub?.payer_uid ?? null;
            const payerName = payerUid ? payerNames[payerUid] : null;
            const householdName = householdNames[e.householdId] ?? "Household";
            const expires = formatDate(sub?.current_period_end ?? sub?.expires_at);
            return (
              <li
                key={e.householdId}
                className="rounded-xl border border-gray-200 dark:border-white/10 px-4 py-3"
              >
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {householdName}
                </p>
                <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
                  {payerName
                    ? `Covered by ${payerName} — renews ${expires}`
                    : `Active subscription — renews ${expires}`}
                </p>
              </li>
            );
          })}
        </ul>

        <div className="px-5 py-4 border-t border-gray-100 dark:border-white/5 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/15 text-gray-700 dark:text-gray-300 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

async function hydrateNames(
  uids: string[],
  setter: (next: NameById) => void,
): Promise<void> {
  if (uids.length === 0) return;
  // Firestore `in` supports up to 30 values per query.
  const chunks: string[][] = [];
  for (let i = 0; i < uids.length; i += 30) {
    chunks.push(uids.slice(i, i + 30));
  }
  const result: NameById = {};
  for (const chunk of chunks) {
    const q = query(
      collection(db, "member_profiles"),
      where(documentId(), "in", chunk),
    );
    const snap = await getDocs(q);
    snap.forEach((d) => {
      const data = d.data() as { nickname?: string; name?: string };
      result[d.id] = data.nickname || data.name || "Someone";
    });
  }
  setter(result);
}

async function hydrateHouseholdNames(
  hhIds: string[],
  setter: (next: HouseholdNameById) => void,
): Promise<void> {
  if (hhIds.length === 0) return;
  const result: HouseholdNameById = {};
  await Promise.all(
    hhIds.map(async (id) => {
      try {
        const ref = doc(db, "households", id);
        const { getDoc } = await import("firebase/firestore");
        const snap = await getDoc(ref);
        const data = snap.data() as { name?: string; household_name?: string } | undefined;
        result[id] = data?.name || data?.household_name || "Household";
      } catch {
        result[id] = "Household";
      }
    }),
  );
  setter(result);
}
